import * as React from 'react';

import { useQuery } from '@apollo/client';

import logger from 'js/app/loggerSingleton';
import { isAuthenticatedUser } from 'js/lib/user';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import SkillMasteryLevelsForCurrentUser from 'bundles/levelsets/levelsets-v2/api/SkillMasteryLevelsForCurrentUser.graphql';
import type {
  SkillMasteryLevelsForCurrentUserQuery,
  SkillMasteryLevelsForCurrentUserQueryVariables,
} from 'bundles/levelsets/levelsets-v2/api/__generated__/SkillMasteryLevelsForCurrentUser';
import { getLevelSetCardItems } from 'bundles/levelsets/levelsets-v2/utils/getLevelSetCardItems';
import { useProgramPinnedSkills } from 'bundles/levelsets/levelsets-v2/utils/useProgramPinnedSkills';
import EnterpriseLevelSetsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseLevelSetsContext';
import { GetDomainOrSmallSubsetSkillData } from 'bundles/page-config-common/queries/GetDomainOrSmallSubsetSkillData';
import type {
  GetDomainOrSmallSubsetSkillDataQuery,
  GetDomainOrSmallSubsetSkillDataQueryVariables,
} from 'bundles/page-config-common/types/enterprise';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';

/**
 * @description This provider is used to fetch the levelSets data for a program.
 */
const EnterpriseLevelSetsProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const program = useProgram();
  const programId = program?.id;

  const enableEnterpriseHomeRefresh = GrowthDiscoveryEpicClient.preview('enableEnterpriseHomeRefresh');

  const {
    data,
    loading: programSkillsLoading,
    error,
  } = useQuery<GetDomainOrSmallSubsetSkillDataQuery, GetDomainOrSmallSubsetSkillDataQueryVariables>(
    GetDomainOrSmallSubsetSkillData,
    {
      skip: !programId || !enableEnterpriseHomeRefresh,
      context: { clientName: 'gatewayGql' },
      variables: { programId: programId! },
    }
  );
  const { data: currentUserMasteryLevelsData, loading: currentUserMasteryLevelsLoading } = useQuery<
    SkillMasteryLevelsForCurrentUserQuery,
    SkillMasteryLevelsForCurrentUserQueryVariables
  >(SkillMasteryLevelsForCurrentUser, {
    skip: !isAuthenticatedUser(),
    context: { clientName: 'gatewayGql' },
  });
  const { loading: pinnedSkillsLoading, pinnedSkills } = useProgramPinnedSkills(programId);

  if (error) {
    // Ignoring errors from  here SkillMasteryLevelsForCurrentUserQuery, because there will be BE logs for these issues
    // and if we can't load the user's mastery levels we can still show the LevelSet cards without this
    logger.error('Error loading skill data for LevelSetsCards:', error);
  }

  const levelSetsLoading = programSkillsLoading || currentUserMasteryLevelsLoading || pinnedSkillsLoading;
  const programSkills = data?.DomainOrSmallSubsetSkillDataQuery?.skills || [];
  const currentUserSkillMasteryLevels =
    currentUserMasteryLevelsData?.SkillMasteryLevels?.querySkillsMasteryLevels?.elements?.map(
      ({ skill, masteryLevel, masteryPercent }) => ({ skillId: skill.id, masteryLevel, masteryPercent })
    ) || [];
  const levelSets = getLevelSetCardItems(programSkills, currentUserSkillMasteryLevels, pinnedSkills);

  return (
    <EnterpriseLevelSetsContext.Provider
      value={{
        levelSets,
        levelSetsLoading,
        hasError: !!error,
      }}
    >
      {children}
    </EnterpriseLevelSetsContext.Provider>
  );
};

export default EnterpriseLevelSetsProvider;
